import type { Column } from '../../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../../lib/http'
import * as Table from '../../../claim-history/simple-table'
import { PageHeader } from '../../components/page-header'
import { SearchInput } from '../../components/search-input'
import { Action, Entry, ProfileMatchingStatus, QueryData, QueryKey, SearchParams, SortDirection, SortKey, sortKeys, State } from './types'
import { useSearchParams } from 'react-router-dom'

const mobileAttemptStatusMapper: Record<ProfileMatchingStatus, string> = {
  [ProfileMatchingStatus.Success]: 'Success',
  [ProfileMatchingStatus.Failed]: 'Failed'
}

const appLabels: Record<string, string> = {
  "lucent-health": "Lucent Health",
  "communicare": "My Health 365"
}

const initialState: State = {
  searchParams: {
    query: "",
    filters: {
      status: [],
      origin: [],
    },
    sort: {
      field: "created_at",
      direction: "desc",
    },
    pagination: {
      page: 0,
      pageSize: 10,
    },
  },
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

let sortMapper: Record<string, string> = {
  name: 'last_name'
}

function isDefaultSearchParams(searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.status.length === initialState.searchParams.filters.status.length &&
    searchParams.filters.status.every(v => initialState.searchParams.filters.status.includes(v)) &&
    searchParams.filters.origin.length === initialState.searchParams.filters.origin.length &&
    searchParams.filters.origin.every(v => initialState.searchParams.filters.origin.includes(v))
  )
}

function buildStateFromURL(searchParams: URLSearchParams): Partial<State> {
  const query = searchParams.get('query') || '';
  return {
    searchParams: {
      ...initialState.searchParams,
      query
    }
  };
}

function buildURLFromState(state: State): URLSearchParams {
  const params = new URLSearchParams();
  if (state.searchParams.query) {
    params.set('query', state.searchParams.query);
  }
  return params;
}

export function ProfileMatchingPage(): JSX.Element {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const syncingFromURL = useRef(false);

  const [state, dispatch] = useReducer(reducer, initialState, (initial) => {
    const urlState = buildStateFromURL(urlSearchParams);
    return {
      ...initial,
      searchParams: {
        ...initial.searchParams,
        ...urlState.searchParams
      }
    };
  });
  const [debouncedSearchQuery] = useDebounceValue(
    state.searchParams.query,
    500,
  );

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'profile-matching',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn({ queryKey }) {
      const [_, { query, filters, sort, pagination }] = queryKey
      const normalizedQuery = query.trim().toLowerCase()

      const sp = new URLSearchParams([
        ["count", pagination.pageSize.toString()],
        ["page", pagination.page.toString()],
        ["on", sortMapper[sort.field] ?? sort.field],
        ["dir", sort.direction],
        ...filters.status.map((v) => ["filters.status", v]),
        ...filters.origin.map((v) => ["filters.origin", v]),
        ...(normalizedQuery.length > 0 ? [["search", normalizedQuery]] : []),
      ])

      return await http.getRequest(`/admin/mobile_attempts?${sp.toString()}`)
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'created_at',
      header: 'Time',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.created_at)}
          </span>
        )
      }
    },
    {
      name: 'first_name',
      header: 'First Name',
      getValue: (row) => {
        if (!row.first_name) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return row.first_name;
      }
    },
    {
      name: 'last_name',
      header: 'Last Name',
      getValue (item) {
        if (!item.last_name) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return (
          <span>
            {item.last_name}
          </span>
        )
      }
    },
    {
      name: 'member_id',
      header: 'Member Id',
      getValue: (row) => {
        if (!row.member_id) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return row.member_id;
      }
    },
    {
      name: 'group_number',
      header: 'Group Number',
      getValue: (row) => {
        if (!row.group_number) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return row.group_number;
      }
    },
    {
      name: 'birth_date',
      header: 'Birthday',
      isSortable: true,
      getValue: (row) => {
        if (!row.birth_date) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return row.birth_date;
      }
    },
    {
      name: 'email',
      header: 'Email',
      getValue: (row) => {
        if (!row.email) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return row.email;
      }
    },
    {
      name: "origin",
      header: "Application",
      gridArea: "max-content",
      getValue: (row) => {
        if (!row.origin) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        const label = appLabels[row.origin] || row.origin;
        const badgeStyle = row.origin === 'lucent-health'
          ? {
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            color: '#1e40af'
          }
          : row.origin === 'communicare'
            ? {
              backgroundColor: 'rgba(249, 115, 22, 0.1)',
              color: '#c2410c'
            }
            : {
              backgroundColor: 'rgba(107, 114, 128, 0.1)',
              color: '#374151'
            };

        return (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.125rem 0.5rem',
              borderRadius: '0.25rem',
              fontSize: '0.75rem',
              fontWeight: '500',
              width: '100%',
              ...badgeStyle
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
        name: 'status',
        header: 'Status',
        gridArea: '110px',
        getValue: (row) => {
            const status = row.status

            if (!status) {
              return <span style={{ color: '#9ca3af' }}>N/A</span>
            }

            const label = mobileAttemptStatusMapper[status] ?? status
            const backgroundColor = status === ProfileMatchingStatus.Success ? '#dcfce7' : status === ProfileMatchingStatus.Failed ? '#fee2e2' : '#f3f4f6'
            const color = status === ProfileMatchingStatus.Success ? '#166534' : status === ProfileMatchingStatus.Failed ? '#991b1b' : '#1f2937'

            return (
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    backgroundColor,
                    color
                }}>
                    {label}
                </span>
            )
        }
    },
    {
      name: 'user_agent',
      header: 'User Agent',
      getValue: (row) => {
        if (!row.user_agent) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return row.user_agent;
      }
    },
    {
      name: 'ip',
      header: 'IP Address',
      getValue: (row) => {
        if (!row.ip) {
          return <span style={{ color: '#9ca3af' }}>N/A</span>
        }

        return row.ip;
      }
    }
  ], [])

  let filters = useMemo(() => {
    return [
      {
        name: 'status',
        value: state.searchParams.filters.status,
        hideSearch: true,
        options: [
          { value: ProfileMatchingStatus.Success, label: mobileAttemptStatusMapper[ProfileMatchingStatus.Success] },
          { value: ProfileMatchingStatus.Failed, label: mobileAttemptStatusMapper[ProfileMatchingStatus.Failed] }
        ]
      },
      {
        name: 'origin',
        value: state.searchParams.filters.origin,
        hideSearch: true,
        options: [
          {value: 'lucent-health', label: appLabels['lucent-health']},
          {value: 'communicare', label: appLabels.communicare}
        ]
      }
    ]
  }, [ state.searchParams.filters ])

  useEffect(() => {
    const urlQuery = urlSearchParams.get('query') || ''
    if (urlQuery !== state.searchParams.query) {
      syncingFromURL.current = true
      dispatch({ type: 'query/set', payload: urlQuery })
    }
  }, [urlSearchParams])

  useEffect(() => {
    if (syncingFromURL.current) {
      syncingFromURL.current = false
      return
    }

    const urlQuery = urlSearchParams.get('query') || ''
    if (urlQuery !== state.searchParams.query) {
      const newParams = buildURLFromState(state)
      setUrlSearchParams(newParams, { replace: true })
    }
  }, [state.searchParams.query, setUrlSearchParams])

  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {
    dispatch({
      type: 'filters/merge',
      payload: {
        [name]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader>
        <h1>
          <span>Profile Matching</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by name, birthday, email, user agent, IP address and status'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No mobile attempts found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  )
}
